import { dia, shapes } from '@joint/core'
import { TemplateRelationType } from '../../../map-editor-entities/schema/templateSchema'
import type { ObjectType } from '../../../map-editor-shared/store/objectTypeStore'
import { routeObjectByType } from './exportUtils'
import { nearestParkingLinks } from './nearestParkingLinks'
import { graphDistanceScale } from './normalizeMapScale'
import { routeLayerToCategory } from '../../../map-editor-shared/lib/layerUtils'
import { resolveLayerToTemplateKey } from '../../../map-editor-shared/lib/layerUtils'
import { log, LogCategory } from '../../../map-editor-shared/lib/logger'
import { getFlag } from '../../../config/featureFlags'
import { getActiveCanvasRenderer } from '../../viewer/canvas/activeRenderer'

interface AutoLinkResult {
    sourceId: string
    targetIds: string[]
    relationKey: string
    maxDistance: number
    sourceCenter: { x: number; y: number }
    skipped?: {
        sourceIds: string[]
        reasons: Record<string, string>
    }
    existingCount?: number
    addedCount?: number
}

/**
 * Parse cardinality string to extract max count
 * @param cardinality - "N" (unlimited) or "1:number" (e.g., "1:1", "1:5")
 * @returns max count or null for unlimited
 */
export function parseCardinality(cardinality: string): number | null {
    if (cardinality === 'N') {
        return null // unlimited
    }

    const match = cardinality.match(/^1:(\d+)$/)
    if (match) {
        return parseInt(match[1], 10)
    }

    log.warn(LogCategory.RELATIONSHIP, `Invalid cardinality format: ${cardinality}`)
    return null
}

/**
 * Get existing relationships for an element's property
 * @param element - The source element
 * @param propertyKey - The property key that stores relationships
 * @returns Array of target IDs (empty array if none)
 */
export function getExistingRelationships(
    element: dia.Element,
    propertyKey: string
): string[] {
    const currentData = element.get('data') || {}
    const currentProps = currentData.properties || {}
    const value = currentProps[propertyKey]

    if (!value) return []
    if (Array.isArray(value)) return value
    return [value]
}

/**
 * Calculate remaining capacity for relationships
 * @param element - The source element
 * @param propertyKey - The property key that stores relationships
 * @param cardinality - The cardinality constraint
 * @returns Remaining slots (null for unlimited)
 */
export function getRemainingCapacity(
    element: dia.Element,
    propertyKey: string,
    cardinality: string
): number | null {
    const maxCount = parseCardinality(cardinality)

    // Unlimited capacity
    if (maxCount === null) return null

    const existing = getExistingRelationships(element, propertyKey)
    const remaining = maxCount - existing.length

    log.debug(LogCategory.RELATIONSHIP, `Capacity check: max=${maxCount}, existing=${existing.length}, remaining=${remaining}`)
    return Math.max(0, remaining)
}

/**
 * Check if a target is already linked
 * @param element - The source element
 * @param propertyKey - The property key that stores relationships
 * @param targetId - The target ID to check
 * @returns true if already linked
 */
export function isAlreadyLinked(
    element: dia.Element,
    propertyKey: string,
    targetId: string
): boolean {
    const existing = getExistingRelationships(element, propertyKey)
    return existing.includes(targetId)
}

/**
 * Auto-link a single source element to matching targets.
 * Accepts pre-filtered candidates and a globalLinkedSet for O(1) duplicate check
 * instead of calling isTargetLinkedGlobally per candidate (which was O(n²)).
 */
export function autoLinkObjects(
    graph: dia.Graph,
    sourceElement: dia.Element,
    _relationKey: string,
    config: TemplateRelationType,
    uuidToTemplateType?: Map<string, string>,
    maxLinksToCreate?: number,
    templateObjectTypes?: Record<string, unknown>,
    prefilteredCandidates?: dia.Element[],
    globalLinkedSet?: Set<string>,
    candidateCenterCache?: Map<string, { x: number; y: number }>
): string[] {
    if (!config.autoLink) return []

    const sourceCenter = sourceElement.getBBox().center()
    const allowDuplicates = config.autoLink.allowDuplicates ?? false
    const existingTargets = getExistingRelationships(sourceElement, config.propertyKey)
    const existingSet = new Set(existingTargets)

    // Use pre-filtered candidates if provided (from autoLinkAllObjects), else filter from graph
    let candidates: dia.Element[]
    if (prefilteredCandidates) {
        candidates = prefilteredCandidates.filter(el => {
            if (el.id === sourceElement.id) return false
            if (!allowDuplicates && existingSet.has(el.id as string)) return false
            if (!allowDuplicates && globalLinkedSet && globalLinkedSet.has(el.id as string)) return false
            return true
        })
    } else {
        // Legacy path: filter from graph (used when called individually)
        candidates = graph.getElements().filter(el => {
            if (el.id === sourceElement.id) return false
            if (!allowDuplicates && existingSet.has(el.id as string)) return false

            if (!allowDuplicates) {
                const { isLinked } = isTargetLinkedGlobally(
                    graph, config, el.id as string, sourceElement.id as string,
                    uuidToTemplateType, templateObjectTypes
                )
                if (isLinked) return false
            }

            const data = el.get('data')
            const typeId = data?.typeId
            const layer = data?.layer
            let templateTypeKey = uuidToTemplateType?.get(typeId) || typeId
            if (!templateTypeKey && layer && /^[cepls]-/i.test(layer)) {
                templateTypeKey = templateObjectTypes
                    ? resolveLayerToTemplateKey(layer, templateObjectTypes)
                    : routeObjectByType(layer)
            }
            return templateTypeKey === config.targetType
        })
    }

    const { strategy, direction } = config.autoLink
    const maxDistance = config.autoLink.maxDistance * graphDistanceScale(graph)
    const linkedIds: string[] = []

    if (strategy === 'nearest') {
        // Filter by distance and direction, cache distance for sorting
        const inRange: Array<{ el: dia.Element; dist: number }> = []
        for (const target of candidates) {
            const cached = candidateCenterCache?.get(target.id as string)
            const targetCenter = cached || target.getBBox().center()
            const dx = targetCenter.x - sourceCenter.x
            const dy = targetCenter.y - sourceCenter.y
            const distance = Math.sqrt(dx * dx + dy * dy)

            if (distance > maxDistance) continue
            if (direction === 'left' && targetCenter.x >= sourceCenter.x) continue
            if (direction === 'right' && targetCenter.x <= sourceCenter.x) continue

            inRange.push({ el: target, dist: distance })
        }

        // Sort by cached distance (no recomputation)
        inRange.sort((a, b) => a.dist - b.dist)

        const maxCount = parseCardinality(config.cardinality)
        let effectiveLimit: number | null = maxCount

        if (maxLinksToCreate !== undefined) {
            effectiveLimit = effectiveLimit === null
                ? maxLinksToCreate
                : Math.min(effectiveLimit, maxLinksToCreate)
        }

        if (effectiveLimit === 1) {
            if (inRange.length > 0) linkedIds.push(inRange[0].el.id as string)
        } else if (effectiveLimit === null) {
            linkedIds.push(...inRange.map(r => r.el.id as string))
        } else {
            linkedIds.push(...inRange.slice(0, effectiveLimit).map(r => r.el.id as string))
        }
    }

    return linkedIds
}

export function updateRelationship(
    element: dia.Element,
    propertyKey: string,
    targetId: string,
    cardinality: string,
    action: 'add' | 'remove'
) {
    const currentData = element.get('data') || {}
    const currentProps = currentData.properties || {}
    const value = currentProps[propertyKey]

    const maxCount = parseCardinality(cardinality)
    let newValue: string | string[] | undefined

    if (maxCount === 1) {
        // Single relationship (1:1)
        if (action === 'add') {
            newValue = targetId
        } else {
            newValue = undefined
        }
    } else {
        // Multiple relationships (1:N or N - unlimited)
        const list = Array.isArray(value) ? [...value] : (value ? [value] : [])

        if (action === 'add') {
            // Check max count limit
            if (maxCount !== null && list.length >= maxCount) {
                log.warn(LogCategory.RELATIONSHIP, `Cannot add more relations. Maximum limit (${maxCount}) reached.`)
                return currentData // Reject addition
            }

            if (!list.includes(targetId)) {
                list.push(targetId)
            }
        } else {
            const index = list.indexOf(targetId)
            if (index > -1) {
                list.splice(index, 1)
            }
        }
        newValue = list.length > 0 ? list : undefined
    }

    const newData = {
        ...currentData,
        properties: {
            ...currentProps,
            [propertyKey]: newValue
        }
    }

    element.set('data', newData)
    return newData
}

/**
 * Auto-link all objects based on template relation types
 */
export function autoLinkAllObjects(
    graph: dia.Graph,
    relationTypes: Record<string, TemplateRelationType>,
    objectTypes: ObjectType[],
    template?: any,
    adjustedDistances?: Record<string, number>
): AutoLinkResult[] {
    const results: AutoLinkResult[] = []
    const skippedSources: Record<string, { sourceIds: string[], reasons: Record<string, string> }> = {}

    log.info(LogCategory.RELATIONSHIP, `autoLinkAllObjects called with ${Object.keys(relationTypes).length} relation types`)

    // Build UUID to template type key mapping
    const uuidToTemplateType = new Map<string, string>()
    if (template?.objectTypes) {

        log.debug(LogCategory.RELATIONSHIP, 'Building UUID to template type mapping...')
        log.debug(LogCategory.RELATIONSHIP, `ObjectTypes in store: ${objectTypes.length}`)
        objectTypes.forEach(objType => {
            log.debug(LogCategory.RELATIONSHIP, `ObjectType: id=${objType.id}, name="${objType.name}"`)
        })
        log.debug(LogCategory.RELATIONSHIP, `Template types: ${Object.keys(template.objectTypes).join(', ')}`)

        objectTypes.forEach(objType => {
            // Try 1: Direct name match with template displayName or name
            let templateEntry = Object.entries(template.objectTypes).find(([_, tmplType]: [string, any]) =>
                tmplType.displayName === objType.name || tmplType.name === objType.name
            )

            // Try 2: If objectType name looks like a layer name, use template-aware resolver.
            // resolveLayerToTemplateKey handles plural-form (light → lights) and fuzzy suffix
            // (cctv → lightCctv) cases that the category-only match cannot.
            if (!templateEntry && /^[cepls]-/i.test(objType.name)) {
                const resolvedKey = resolveLayerToTemplateKey(objType.name, template.objectTypes)
                log.debug(LogCategory.RELATIONSHIP, `Resolving layer name: "${objType.name}" -> "${resolvedKey}"`)
                if (resolvedKey && template.objectTypes[resolvedKey]) {
                    templateEntry = [resolvedKey, template.objectTypes[resolvedKey]] as [string, any]
                }
            }

            if (templateEntry) {
                uuidToTemplateType.set(objType.id, templateEntry[0])
                log.debug(LogCategory.RELATIONSHIP, `UUID mapping: ${objType.id} -> ${templateEntry[0]} (${objType.name})`)
            } else {
                log.debug(LogCategory.RELATIONSHIP, `No template match for: ${objType.id} (${objType.name})`)
            }
        })

        log.debug(LogCategory.RELATIONSHIP, `Total mappings created: ${uuidToTemplateType.size}`)
    }

    // 반경은 표준 스케일 px — 이 graph 좌표로 환산한다.
    const distanceScale = graphDistanceScale(graph)

    // Cache all elements once and classify by template type key
    const allElements = graph.getElements()
    const elementsByType = new Map<string, dia.Element[]>()
    const elementCenterCache = new Map<string, { x: number; y: number }>()

    for (const el of allElements) {
        const data = el.get('data')
        const typeId = data?.typeId
        const layer = data?.layer
        let templateTypeKey = uuidToTemplateType.get(typeId) || typeId
        if (!templateTypeKey && layer && /^[cepls]-/i.test(layer)) {
            templateTypeKey = template?.objectTypes
                ? resolveLayerToTemplateKey(layer, template.objectTypes)
                : routeObjectByType(layer)
        }
        if (templateTypeKey) {
            if (!elementsByType.has(templateTypeKey)) elementsByType.set(templateTypeKey, [])
            elementsByType.get(templateTypeKey)!.push(el)
        }
        // Cache center position for distance calculations
        const center = el.getBBox().center()
        elementCenterCache.set(el.id as string, { x: center.x, y: center.y })
    }

    // Process each relation type with autoLink config
    Object.entries(relationTypes).forEach(([key, config]) => {
        if (!config.autoLink) return

        const allowDuplicates = config.autoLink.allowDuplicates ?? false

        // Get source and target elements from cached classification (O(1) lookup)
        const sourceElements = elementsByType.get(config.sourceType) || []
        const targetCandidates = elementsByType.get(config.targetType) || []

        log.info(LogCategory.RELATIONSHIP, `Found ${sourceElements.length} source elements for ${key}`)

        // Build global linked set: which target IDs are already linked by ANY source
        // This replaces O(n²) isTargetLinkedGlobally calls with O(1) Set lookup
        const globalLinkedSet = new Set<string>()
        if (!allowDuplicates) {
            for (const srcEl of sourceElements) {
                const existing = getExistingRelationships(srcEl, config.propertyKey)
                for (const targetId of existing) {
                    globalLinkedSet.add(targetId)
                }
            }
        }

        if (!skippedSources[key]) {
            skippedSources[key] = { sourceIds: [], reasons: {} }
        }

        const maxDistance = (adjustedDistances?.[key] || config.autoLink.maxDistance) * distanceScale
        const direction = config.autoLink.direction
        // 전방위 CCTV → 주차구역: 최소 1개 보장을 위해 반경 밖 쌍도 모은다.
        const cctvParking = !allowDuplicates &&
            routeLayerToCategory(config.sourceType) === 'cctv' &&
            routeLayerToCategory(config.targetType) === 'parkingLocation'

        // Collect every (source, target) pair inside the radius, then assign
        // globally nearest-first so a source processed early can't grab a target
        // that sits much closer to another source.
        const capacity = new Map<string, number | null>()
        const existingCounts = new Map<string, number>()
        const pairs: Array<{ src: dia.Element; tgtId: string; dist: number }> = []
        for (const sourceElement of sourceElements) {
            const srcId = sourceElement.id as string
            const remainingCapacity = getRemainingCapacity(sourceElement, config.propertyKey, config.cardinality)
            if (remainingCapacity !== null && remainingCapacity === 0) {
                skippedSources[key].sourceIds.push(srcId)
                skippedSources[key].reasons[srcId] = 'max_capacity_reached'
                continue
            }
            const existing = getExistingRelationships(sourceElement, config.propertyKey)
            capacity.set(srcId, remainingCapacity)
            existingCounts.set(srcId, existing.length)
            const existingSet = new Set(existing)
            const srcCenter = elementCenterCache.get(srcId)!
            for (const target of targetCandidates) {
                const tgtId = target.id as string
                if (tgtId === srcId) continue
                if (!allowDuplicates && (existingSet.has(tgtId) || globalLinkedSet.has(tgtId))) continue
                const tgtCenter = elementCenterCache.get(tgtId)!
                const dx = tgtCenter.x - srcCenter.x
                const dy = tgtCenter.y - srcCenter.y
                const dist = Math.sqrt(dx * dx + dy * dy)
                if (dist > maxDistance && !cctvParking) continue
                if (direction === 'left' && tgtCenter.x >= srcCenter.x) continue
                if (direction === 'right' && tgtCenter.x <= srcCenter.x) continue
                pairs.push({ src: sourceElement, tgtId, dist })
            }
        }
        pairs.sort((a, b) => a.dist - b.dist)

        const linkedBySource = cctvParking
            ? nearestParkingLinks(pairs.map(pair => ({ sourceId: String(pair.src.id), targetId: pair.tgtId, distance: pair.dist })), existingCounts, capacity, maxDistance)
            : new Map<string, string[]>()
        for (const { src, tgtId } of cctvParking ? [] : pairs) {
            const srcId = src.id as string
            if (!allowDuplicates && globalLinkedSet.has(tgtId)) continue
            const cap = capacity.get(srcId)
            const linked = linkedBySource.get(srcId) ?? []
            if (cap !== null && cap !== undefined && linked.length >= cap) continue
            if (linked.includes(tgtId)) continue
            linked.push(tgtId)
            linkedBySource.set(srcId, linked)
            if (!allowDuplicates) globalLinkedSet.add(tgtId)
        }

        for (const sourceElement of sourceElements) {
            const linkedIds = linkedBySource.get(sourceElement.id as string) ?? []
            const existingCount = existingCounts.get(sourceElement.id as string) ?? 0

            if (linkedIds.length > 0) {
                const maxCount = parseCardinality(config.cardinality)

                if (maxCount === 1) {
                    updateRelationship(sourceElement, config.propertyKey, linkedIds[0], config.cardinality, 'add')
                } else {
                    for (const targetId of linkedIds) {
                        updateRelationship(sourceElement, config.propertyKey, targetId, config.cardinality, 'add')
                    }
                }

                if (config.inversePropertyKey) {
                    for (const targetId of linkedIds) {
                        const targetElement = graph.getCell(targetId) as dia.Element
                        if (targetElement) {
                            updateRelationship(
                                targetElement, config.inversePropertyKey!,
                                sourceElement.id as string, config.cardinality, 'add'
                            )
                        }
                    }
                }

                const srcCenter = elementCenterCache.get(sourceElement.id as string)!
                results.push({
                    sourceId: sourceElement.id as string,
                    targetIds: linkedIds,
                    relationKey: key,
                    maxDistance,
                    sourceCenter: srcCenter,
                    existingCount,
                    addedCount: linkedIds.length
                })
            }
        }

        if (skippedSources[key].sourceIds.length > 0) {
            log.debug(LogCategory.RELATIONSHIP, `Skipped ${skippedSources[key].sourceIds.length} sources for ${key}:`, skippedSources[key])
        }
    })

    // Add aggregated skip information to results
    const totalSkipped = Object.values(skippedSources).reduce((sum, skip) => sum + skip.sourceIds.length, 0)
    if (totalSkipped > 0) {
        log.info(LogCategory.RELATIONSHIP, `Total skipped sources: ${totalSkipped}`)
        // Add skip info to first result or create a summary result
        if (results.length > 0) {
            results[0].skipped = Object.entries(skippedSources).reduce((acc, [_key, skip]) => {
                if (skip.sourceIds.length > 0) {
                    acc.sourceIds.push(...skip.sourceIds)
                    Object.assign(acc.reasons, skip.reasons)
                }
                return acc
            }, { sourceIds: [] as string[], reasons: {} as Record<string, string> })
        }
    }

    log.info(LogCategory.RELATIONSHIP, `Total auto-link results: ${results.length}`)
    return results
}

/**
 * Check if a target is already linked by any other object (global uniqueness check)
 * @param graph - The JointJS graph
 * @param relationConfig - The relation type configuration
 * @param targetId - The target ID to check
 * @param excludeSourceId - Source ID to exclude from check (for current object)
 * @returns { isLinked: boolean, linkedBySourceId?: string }
 */
export function isTargetLinkedGlobally(
    graph: dia.Graph,
    relationConfig: TemplateRelationType,
    targetId: string,
    excludeSourceId?: string,
    uuidToTemplateType?: Map<string, string>,
    templateObjectTypes?: Record<string, unknown>
): { isLinked: boolean; linkedBySourceId?: string } {
    const { sourceType, propertyKey, autoLink } = relationConfig
    const allowDuplicates = autoLink?.allowDuplicates ?? false

    // If duplicates are allowed, skip check
    if (allowDuplicates) {
        return { isLinked: false }
    }

    // Find all source elements of this relation type
    const sourceElements = graph.getElements().filter(el => {
        const data = el.get('data')
        const typeId = data?.typeId || data?.type
        const layer = data?.layer

        // Try to get template type key
        let templateTypeKey = uuidToTemplateType?.get(typeId) || typeId

        // If no typeId but has layer, use template-aware resolution
        if (!templateTypeKey && layer && /^[cepls]-/i.test(layer)) {
            if (templateObjectTypes) {
                templateTypeKey = resolveLayerToTemplateKey(layer, templateObjectTypes)
            } else {
                templateTypeKey = routeObjectByType(layer)
            }
        }

        return templateTypeKey === sourceType && el.id !== excludeSourceId
    })

    // Check if any source already has this target linked
    for (const sourceEl of sourceElements) {
        const existingTargets = getExistingRelationships(sourceEl, propertyKey)
        if (existingTargets.includes(targetId)) {
            return {
                isLinked: true,
                linkedBySourceId: sourceEl.id as string
            }
        }
    }

    return { isLinked: false }
}

/**
 * Create radius visualization circles for auto-link preview
 */
export function createRadiusCircles(
    paper: dia.Paper,
    results: AutoLinkResult[]
): dia.Element[] {
    const circles: dia.Element[] = []
    const colorMap = new Map<string, string>()
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4']
    let colorIndex = 0

    results.forEach(result => {
        // Get color for this source type
        if (!colorMap.has(result.sourceId)) {
            colorMap.set(result.sourceId, colors[colorIndex % colors.length])
            colorIndex++
        }
        const color = colorMap.get(result.sourceId)!

        // Create circle element with proper JointJS shape
        const circle = new shapes.standard.Circle({
            position: {
                x: result.sourceCenter.x - result.maxDistance,
                y: result.sourceCenter.y - result.maxDistance
            },
            size: {
                width: result.maxDistance * 2,
                height: result.maxDistance * 2
            },
            attrs: {
                body: {
                    fill: 'transparent',
                    stroke: color,
                    strokeWidth: 2,
                    strokeDasharray: '5,5',
                    opacity: 0.6
                }
            }
        })

        // Mark as radius circle for easy identification and removal
        circle.set('isRadiusCircle', true)

        paper.model.addCell(circle)
        circles.push(circle)
    })

    return circles
}

/**
 * Remove all radius circles from the graph
 */
export function clearRadiusCircles(graph: dia.Graph): void {
    const circles = graph.getElements().filter(el => el.get('isRadiusCircle') === true)
    circles.forEach(circle => circle.remove())

    if (circles.length > 0) {
        log.debug(LogCategory.RELATIONSHIP, `Removed ${circles.length} radius circles`)
    }
}

// Color palette per relation key
const RELATION_COLORS: Record<string, string> = {
    'cctv_monitoring': '#3B82F6',       // 파란색: CCTV → 주차면
    'cctv_left_connection': '#8B5CF6',  // 보라색: CCTV → CCTV
    'emergency_bell_cctv': '#10B981',   // 초록색: 비상벨 → CCTV
}
const DEFAULT_LINK_COLOR = '#F59E0B' // 주황색 기본

/**
 * Create visual link lines + target highlights for auto-link results.
 * Returns a cleanup function that removes all visualization elements.
 */
export function createAutoLinkVisualization(
    paper: dia.Paper,
    graph: dia.Graph,
    results: AutoLinkResult[]
): () => void {
    // CANVAS_EDITOR 모드: SVG cell view 도, 링크 셀 렌더링도 없다(링크는
    // cellsToCanvasElements 에서 걸러진다). 그래프를 건드리는 대신 캔버스
    // 렌더러에 결과 오버레이를 넘긴다.
    const canvasRenderer = getFlag('CANVAS_EDITOR') ? getActiveCanvasRenderer() : null
    if (canvasRenderer) {
        const lines: { x1: number; y1: number; x2: number; y2: number; color: string }[] = []
        const linkedIds = new Set<string>()

        results.forEach(result => {
            const color = RELATION_COLORS[result.relationKey] || DEFAULT_LINK_COLOR
            const sourceElement = graph.getCell(result.sourceId) as dia.Element
            if (!sourceElement) return
            const src = sourceElement.getBBox().center()

            result.targetIds.forEach(targetId => {
                const targetElement = graph.getCell(targetId) as dia.Element
                if (!targetElement) return
                const tgt = targetElement.getBBox().center()
                lines.push({ x1: src.x, y1: src.y, x2: tgt.x, y2: tgt.y, color })
                linkedIds.add(targetId)
            })
        })

        canvasRenderer.setAutoLinkResultOverlay({ linkedIds: Array.from(linkedIds), lines })
        log.info(LogCategory.RELATIONSHIP, `Auto-link visualization (canvas): ${lines.length} links, ${linkedIds.size} highlighted targets`)

        return () => {
            canvasRenderer.setAutoLinkResultOverlay(null)
            log.debug(LogCategory.RELATIONSHIP, 'Auto-link visualization cleaned up')
        }
    }

    const createdLinks: dia.Link[] = []
    const highlightedViews: Set<string> = new Set()

    results.forEach(result => {
        const color = RELATION_COLORS[result.relationKey] || DEFAULT_LINK_COLOR
        const sourceElement = graph.getCell(result.sourceId) as dia.Element
        if (!sourceElement) return

        result.targetIds.forEach(targetId => {
            const targetElement = graph.getCell(targetId) as dia.Element
            if (!targetElement) return

            // Create link line between source and target
            const link = new shapes.standard.Link({
                source: { id: result.sourceId },
                target: { id: targetId },
                attrs: {
                    line: {
                        stroke: color,
                        strokeWidth: 1,
                        strokeDasharray: '4,3',
                        targetMarker: {
                            type: 'path',
                            d: 'M 10 -5 0 0 10 5 z',
                            fill: color,
                            stroke: color
                        }
                    }
                },
                z: -1
            })
            link.set('isAutoLinkVisualization', true)
            graph.addCell(link)
            createdLinks.push(link)

            // Highlight target element with orange
            const view = paper.findViewByModel(targetElement)
            if (view && view.el) {
                view.el.classList.add('autolink-connected')
                highlightedViews.add(targetId)
            }
        })
    })

    log.info(LogCategory.RELATIONSHIP, `Auto-link visualization: ${createdLinks.length} links, ${highlightedViews.size} highlighted targets`)

    // Return cleanup function
    return () => {
        createdLinks.forEach(link => link.remove())
        highlightedViews.forEach(targetId => {
            const el = graph.getCell(targetId) as dia.Element
            if (el) {
                const view = paper.findViewByModel(el)
                if (view && view.el) {
                    view.el.classList.remove('autolink-connected')
                }
            }
        })
        log.debug(LogCategory.RELATIONSHIP, 'Auto-link visualization cleaned up')
    }
}
